import { ajax } from 'cadenza/api-client/api';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import { MIMETYPE_JSON } from 'cadenza/model/media-type';
import type { RepositoryItemId } from 'cadenza/repository/model/repository-item-id';
import { defineRepositoryItemId } from 'cadenza/repository/model/repository-item-id';
import type { ObjecttypeDataProviderName } from 'cadenza/repository/model/objecttype-data-provider-name';

export type GeocoderDataProviderType = 'GEOCODER_ADDRESS' | 'GEOCODER_POI' | 'GEOCODER_IP';
export type DataProviderType = GeocoderDataProviderType | 'COORDINATE' | 'OBJECTTYPE' | 'WHAT_3_WORDS';

/** @see net.disy.cadenza.locationfinder.web.dto.admin.LocationFinderAdminSettingsDto.java */
export interface LocationFinderAdminSettingsDto {
  dataProviders?: LocationFinderDataProviderDto[];
  coordinateProviders?: CoordinateProviderAdminPageDto[];
  geocoderServices?: GeocoderServiceAdminPageDto[];
  what3wordsServices?: What3WordsServiceAdminPageDto[];
  placeholderText?: string;
  useCurrentBBox?: boolean;
  providersWithMissingObjecttype: DataProviderId[];
  providersWithMissingAttributes: DataProviderId[];
}

interface BaseDataProviderId<T extends DataProviderType> {
  dataProviderType: T;
}

interface GeocoderDataProviderId extends BaseDataProviderId<GeocoderDataProviderType> {
  name: string;
}
interface CoordinateDataProviderId extends BaseDataProviderId<'COORDINATE'> {
  name: string;
}
export type ObjecttypeDataProviderId =
  BaseDataProviderId<'OBJECTTYPE'>
  & RepositoryItemId<'LOCATION_FINDER_OBJECTTYPE_DATA_PROVIDER', ObjecttypeDataProviderName>;
export const ObjecttypeDataProviderId = defineRepositoryItemId<ObjecttypeDataProviderId>(
  'LOCATION_FINDER_OBJECTTYPE_DATA_PROVIDER',
  (repositoryName, itemName) => ({
    itemType: 'LOCATION_FINDER_OBJECTTYPE_DATA_PROVIDER',
    dataProviderType: 'OBJECTTYPE',
    repositoryName,
    itemName
  }));

interface What3WordsDataProviderId extends BaseDataProviderId<'WHAT_3_WORDS'> {
  name: string;
}

export type DataProviderId = GeocoderDataProviderId | CoordinateDataProviderId | ObjecttypeDataProviderId | What3WordsDataProviderId;

export function dataProviderIdAsString (dataProviderId: DataProviderId) {
  return isObjecttypeDataProviderId(dataProviderId)
    ? dataProviderId.repositoryName + '/' + dataProviderId.itemName
    : dataProviderId.name;
}

function isObjecttypeDataProviderId (id: DataProviderId): id is ObjecttypeDataProviderId {
  return ('repositoryName' in id);
}

export interface DataProviderAttribute {
  attributeName: string;
  showOnly: boolean;
}

export interface LocationFinderDataProviderDto<T extends DataProviderId = DataProviderId> {
  dataProviderId: T;
  dataProviderType: T['dataProviderType']; // do not base the logic on this field, use the type from id
  printName: string;
  sourceName: string;
  order: number;
  enabled: boolean;
}

/** @see net.disy.cadenza.locationfinder.web.dto.admin.ObjecttypeDataProviderDto.java */
export interface ObjecttypeDataProviderDto extends LocationFinderDataProviderDto<ObjecttypeDataProviderId> {
  searchReplacements: DataProviderSearchReplacement[];
  attributes: DataProviderAttribute[];
  searchStrategy: string;
  searchStringFormat?: string;
  listItemFormat?: string;
  listItemHeadline?: string;
  maxCount?: number;
  maxZoomScale?: number;
}

/** @see net.disy.cadenza.locationfinder.web.dto.admin.What3WordsDataProviderDto.java */
export interface What3WordsDataProviderDto extends LocationFinderDataProviderDto<What3WordsDataProviderId> {
  listItemHeadline?: string;
  maxZoomScale?: number;
  coordinateProviderConfigId?: string;
}

export type CoordinateDataProviderDto = LocationFinderDataProviderDto<CoordinateDataProviderId>;

export interface GeocoderDataProviderDto extends LocationFinderDataProviderDto<GeocoderDataProviderId> {
  listItemHeadline: string;
  maxZoomScale: number;
  maxCount?: number;
  searchStringFormat: string; // searchField
  listItemFormat: string; // resultList
  searchReplacements: DataProviderSearchReplacement[];
}

/** @see net.disy.cadenza.locationfinder.web.model.DataProviderSearchReplacement.java */
export interface DataProviderSearchReplacement {
  from: string;
  to: string;
}

export interface DataProviderSource {
  id: string;
  text?: string;
}

export interface GeocoderServiceAdminPageDto extends DataProviderSource {
  dataProviderTypes: GeocoderDataProviderType[];
}

export interface What3WordsServiceAdminPageDto extends DataProviderSource {}

export interface CoordinateProviderAdminPageDto extends DataProviderSource {
  resolvers: CoordinateResolverAdminPageDto[];
}

export interface CoordinateResolverAdminPageDto {
  listItemDescription: string;
  srs: string;
}

/** @see net.disy.cadenza.locationfinder.web.dto.LocationFinderSettingsDto.java */
export interface LocationFinderSettingsDto {
  placeholderText?: string;
  useCurrentBBox: boolean;
}

/** @see net.disy.cadenza.geocoder.dto.GeocodedAddressDto.java */
export interface GeocodedAddressDto {
  continent?: string;
  countryCode?: string;
  countrySubdivision?: string;
  countrySecondarySubdivision?: string;
  county?: string;
  municipality?: string;
  municipalitySubdivision?: string;
  postalCode?: string;
  cityDistrict?: string;
  neighborhood?: string;
  street?: string;
  houseNumber?: string;
}

export function fetchLocationFinderAdminSettings (): Promise<LocationFinderAdminSettingsDto> {
  const url = '/admin/location-finder/config';
  return ajax<LocationFinderAdminSettingsDto>(url, {
    method: 'GET'
  });
}

export function addLocationFinderDataProvider (locationFinderDto: LocationFinderDataProviderDto): Promise<LocationFinderDataProviderDto> {
  const url = '/admin/location-finder/providers';
  return ajax<LocationFinderDataProviderDto>(url, {
    method: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(locationFinderDto)
  });
}

export function editLocationFinderDataProvider (locationFinderDto: LocationFinderDataProviderDto): Promise<LocationFinderDataProviderDto> {
  const url = '/admin/location-finder/providers';
  return ajax<LocationFinderDataProviderDto>(url, {
    method: 'PUT',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(locationFinderDto)
  });
}

export function deleteLocationFinderDataProvider (dataProviderId: DataProviderId): Promise<void> {
  return ajax<void>(getDataProviderUrl(dataProviderId), {
    method: 'DELETE'
  });
}

export function moveLocationFinderDataProvider (dataProviderId: DataProviderId, nextDataProviderId?: DataProviderId): Promise<LocationFinderDataProviderDto> {
  return ajax<LocationFinderDataProviderDto>(`${getDataProviderUrl(dataProviderId)}/next-sibling`, {
    method: 'PUT',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(nextDataProviderId)
  });
}

function getDataProviderUrl (dataProviderId: DataProviderId) {
  return isObjecttypeDataProviderId(dataProviderId)
    ? `/admin/location-finder/providers/${dataProviderId.repositoryName}/${dataProviderId.dataProviderType}/${dataProviderId.itemName}`
    : `/admin/location-finder/providers/${dataProviderId.dataProviderType}/${dataProviderId.name}`;
}

export function getDataProviderName (dataProviderId: DataProviderId) {
  return isObjecttypeDataProviderId(dataProviderId) ? dataProviderId.itemName : dataProviderId.name;
}

export function setLocationFinderSetting (settings: LocationFinderSettingsDto): Promise<LocationFinderSettingsDto> {
  const url = '/admin/location-finder/config';
  return ajax<LocationFinderSettingsDto>(url, {
    method: 'PUT',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(settings)
  });
}

export function exportLocationFinderConfiguration () {
  return ajax<WorkOrderId>('/admin/location-finder/export-work-orders', {
    method: 'POST'
  });
}

export function importLocationFinderConfiguration (fileName: string, signature: string) {
  const urlParams = new URLSearchParams({ fileName, signature });
  return ajax<WorkOrderId>(`/admin/location-finder/import-work-orders?${urlParams}`, {
    method: 'POST'
  });
}
